// MD5 (RFC 1321). The browser's crypto.subtle has no MD5, and some storage
// services want the MD5 of a file before they accept an upload.

const K = new Int32Array(64);
for (let i = 0; i < 64; i++) {
  K[i] = Math.floor(Math.abs(Math.sin(i + 1)) * 4294967296) | 0;
}

// prettier-ignore
const S = [
  7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22,
  5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20,
  4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23,
  6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21,
];

export class Md5 {
  private a = 0x67452301;
  private b = 0xefcdab89 | 0;
  private c = 0x98badcfe | 0;
  private d = 0x10325476;
  // bytes that do not fill a block yet
  private buf = new Uint8Array(64);
  private bufLen = 0;
  // bytes hashed so far
  private total = 0;
  private m = new Int32Array(16);

  update(data: Uint8Array): void {
    let n = data.length;
    let i = 0;
    this.total += n;
    if (this.bufLen > 0) {
      let take = Math.min(64 - this.bufLen, n);
      this.buf.set(data.subarray(0, take), this.bufLen);
      this.bufLen += take;
      i = take;
      if (this.bufLen < 64) {
        return;
      }
      this.block(this.buf, 0);
      this.bufLen = 0;
    }
    for (; i + 64 <= n; i += 64) {
      this.block(data, i);
    }
    if (i < n) {
      this.buf.set(data.subarray(i), 0);
      this.bufLen = n - i;
    }
  }

  private block(p: Uint8Array, off: number): void {
    let m = this.m;
    for (let j = 0; j < 16; j++) {
      let o = off + j * 4;
      m[j] = p[o] | (p[o + 1] << 8) | (p[o + 2] << 16) | (p[o + 3] << 24);
    }
    let a = this.a;
    let b = this.b;
    let c = this.c;
    let d = this.d;
    for (let i = 0; i < 64; i++) {
      let f: number;
      let g: number;
      if (i < 16) {
        f = (b & c) | (~b & d);
        g = i;
      } else if (i < 32) {
        f = (d & b) | (~d & c);
        g = (5 * i + 1) & 15;
      } else if (i < 48) {
        f = b ^ c ^ d;
        g = (3 * i + 5) & 15;
      } else {
        f = c ^ (b | ~d);
        g = (7 * i) & 15;
      }
      let t = (a + f + K[i] + m[g]) | 0;
      let s = S[i];
      a = d;
      d = c;
      c = b;
      b = (b + ((t << s) | (t >>> (32 - s)))) | 0;
    }
    this.a = (this.a + a) | 0;
    this.b = (this.b + b) | 0;
    this.c = (this.c + c) | 0;
    this.d = (this.d + d) | 0;
  }

  /** The hash as 32 lowercase hex digits. Call it once, after the last update(). */
  digest(): string {
    let total = this.total;
    let padLen = (this.bufLen < 56 ? 56 : 120) - this.bufLen;
    let pad = new Uint8Array(padLen + 8);
    pad[0] = 0x80;
    // the length in bits, 64-bit little endian
    let view = new DataView(pad.buffer);
    view.setUint32(padLen, (total * 8) >>> 0, true);
    view.setUint32(padLen + 4, Math.floor(total / 0x20000000) >>> 0, true);
    this.update(pad);
    let out = new DataView(new ArrayBuffer(16));
    out.setInt32(0, this.a, true);
    out.setInt32(4, this.b, true);
    out.setInt32(8, this.c, true);
    out.setInt32(12, this.d, true);
    let hex = "";
    for (let i = 0; i < 16; i++) {
      hex += out.getUint8(i).toString(16).padStart(2, "0");
    }
    return hex;
  }
}

export function md5Hex(data: Uint8Array): string {
  let h = new Md5();
  h.update(data);
  return h.digest();
}

const md5ChunkSize = 4 << 20;

/** MD5 of a file, read a piece at a time so that a big file does not have to fit in memory. */
export async function md5OfBlob(blob: Blob): Promise<string> {
  let h = new Md5();
  for (let off = 0; off < blob.size; off += md5ChunkSize) {
    let buf = await blob.slice(off, off + md5ChunkSize).arrayBuffer();
    h.update(new Uint8Array(buf));
  }
  return h.digest();
}
